import type { usePackageSetupStep } from './PackageSetupStep/usePackageSetupStep';
import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import type { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import type { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import type { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import type { useTaxesFeesStep } from './TaxesFeesStep/useTaxesFeesStep';
import { useStepClearance } from './useStepClearance';

export interface UseWizardStepClearanceParams {
  activeStep: number;
  packageSetupStep: ReturnType<typeof usePackageSetupStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomRatesStep: ReturnType<typeof useRoomRatesStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  submissionStep: ReturnType<typeof useSubmissionStep>;
  taxesFeesStep: ReturnType<typeof useTaxesFeesStep>;
  trimmedSalesforceOfferId: string;
}

/** Wires the wizard's step data into `useStepClearance`, and drops a failed submission's errors once any of it is edited. */
export function useWizardStepClearance({
  activeStep,
  packageSetupStep,
  propertyStep,
  ratePlanStep,
  roomRatesStep,
  roomsStep,
  submissionStep,
  taxesFeesStep,
  trimmedSalesforceOfferId,
}: UseWizardStepClearanceParams) {
  // What the CM can edit on each step, by step index (see STEPS); Prerequisites, Capacity and Submission have none.
  const stepData = [
    trimmedSalesforceOfferId,
    undefined,
    [propertyStep.selectedPropertyId, propertyStep.propertyFormData],
    [roomsStep.roomDrafts, packageSetupStep.assetRoomKeys],
    ratePlanStep.ratePlanDrafts,
    roomRatesStep.selectedRoomRatePlanIds,
    undefined,
    taxesFeesStep.taxesFeesDraft,
    [packageSetupStep.flexibleNightsById, packageSetupStep.packageRoomTypeIds],
    undefined,
  ];

  return useStepClearance({
    activeStep,
    // A submit attempt creates (and on failure rolls back) the very drafts being compared, and a
    // rollback error leaves them changed on purpose, so neither counts as the CM editing.
    isEditDetectionPaused:
      submissionStep.isSubmitting || submissionStep.hasUnresolvedRollbackErrors || submissionStep.hasSucceeded,
    // A failed submission's errors describe the data it ran against, so any edit upstream makes them stale.
    onStepEdited: submissionStep.clearFailure,
    stepData,
  });
}
